import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCard, CheckCircle, AlertCircle, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE_URL } from '@/lib/authFetch';

// Chave pública Mercado Pago. Em produção use VITE_MP_PUBLIC_KEY no .env (APP_USR-...).
// Fallback mantido para não alterar comportamento em produção já no ar.
const MP_PUBLIC_KEY = import.meta.env.VITE_MP_PUBLIC_KEY || "APP_USR-8fc95170-333e-493f-9ca5-a3d3ba1c59c8";

interface CheckoutProps {
  plano: {
    id: string;
    nome: string;
    valor: number;
    descricao: string;
  };
  onSuccess: (data: any) => void;
  onCancel: () => void;
}

declare global {
  interface Window {
    MercadoPago: any;
  }
}

const CheckoutTransparente = ({ plano, onSuccess, onCancel }: CheckoutProps) => {
  const [loading, setLoading] = useState(false);
  const [mpReady, setMpReady] = useState(false);
  const [step, setStep] = useState<'form' | 'processing' | 'success' | 'error'>('form');
  const [errorMessage, setErrorMessage] = useState('');
  const mpRef = useRef<any>(null);
  const cardFormRef = useRef<any>(null);

  // Dados do formulário
  const [formData, setFormData] = useState({
    cardNumber: '',
    cardExpiry: '',
    cardCvv: '',
    cardName: '',
    cpf: '',
    email: '',
    installments: '1',
    issuer: ''
  });

  const [cardInfo, setCardInfo] = useState({
    paymentMethodId: '',
    issuerId: '',
    installments: [] as any[]
  });

  // Carregar SDK do Mercado Pago
  useEffect(() => {
    // Em produção use chave de produção do Mercado Pago (HTTPS/domínio)
    const loadMP = async () => {
      if (window.MercadoPago) {
        mpRef.current = new window.MercadoPago(MP_PUBLIC_KEY, { locale: 'pt-BR' });
        setMpReady(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://sdk.mercadopago.com/js/v2';
      script.async = true;
      script.onload = () => {
        mpRef.current = new window.MercadoPago(MP_PUBLIC_KEY, { locale: 'pt-BR' });
        setMpReady(true);
      };
      document.body.appendChild(script);
    };
    loadMP();
  }, []);

  // Buscar email do usuário logado
  useEffect(() => {
    const fetchUserEmail = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await fetch(`${API_BASE_URL}/users/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const userData = await response.json();
            if (userData.email) {
              setFormData(prev => ({ ...prev, email: userData.email }));
            }
          }
        } catch {
          // Erro ao buscar email do usuário
        }
      }
    };
    fetchUserEmail();
  }, []);

  // Identificar bandeira do cartão
  const identifyCard = async (cardNumber: string) => {
    if (!mpRef.current || cardNumber.replace(/\s/g, '').length < 6) return;

    try {
      const bin = cardNumber.replace(/\s/g, '').substring(0, 6);
      const paymentMethods = await mpRef.current.getPaymentMethods({ bin });
      
      if (paymentMethods.results && paymentMethods.results.length > 0) {
        const pm = paymentMethods.results[0];
        setCardInfo(prev => ({
          ...prev,
          paymentMethodId: pm.id,
          issuerId: pm.issuer?.id || ''
        }));

        // Buscar parcelas
        const installments = await mpRef.current.getInstallments({
          amount: String(plano.valor),
          bin: bin,
          paymentTypeId: 'credit_card'
        });

        if (installments && installments.length > 0) {
          setCardInfo(prev => ({
            ...prev,
            installments: installments[0].payer_costs || []
          }));
        }
      }
    } catch {
      // Erro ao identificar cartão
    }
  };

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(' ') : value;
  };

  const formatExpiry = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  const formatCPF = (value: string) => {
    const v = value.replace(/\D/g, '');
    if (v.length <= 11) {
      return v.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return value;
  };

  const handleInputChange = (field: string, value: string) => {
    let formattedValue = value;
    
    if (field === 'cardNumber') {
      formattedValue = formatCardNumber(value);
      if (formattedValue.replace(/\s/g, '').length >= 6) {
        identifyCard(formattedValue);
      }
    } else if (field === 'cardExpiry') {
      formattedValue = formatExpiry(value);
    } else if (field === 'cpf') {
      formattedValue = formatCPF(value);
    } else if (field === 'cardCvv') {
      formattedValue = value.replace(/\D/g, '').substring(0, 4);
    }
    
    setFormData(prev => ({ ...prev, [field]: formattedValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mpReady || !mpRef.current) {
      toast.error('Aguarde o carregamento do sistema de pagamento');
      return;
    }

    // Validações
    const cardNumberClean = formData.cardNumber.replace(/\s/g, '');
    if (cardNumberClean.length < 13) {
      toast.error('Número do cartão inválido');
      return;
    }
    if (!formData.cardExpiry || formData.cardExpiry.length < 5) {
      toast.error('Data de validade inválida');
      return;
    }
    if (!formData.cardCvv || formData.cardCvv.length < 3) {
      toast.error('CVV inválido');
      return;
    }
    if (!formData.cardName) {
      toast.error('Nome do titular é obrigatório');
      return;
    }
    if (!formData.cpf || formData.cpf.replace(/\D/g, '').length < 11) {
      toast.error('CPF inválido');
      return;
    }

    setLoading(true);
    setStep('processing');

    try {
      // Normalizar validade: MM/AA ou M/AA → mês 2 dígitos, ano 4 dígitos (exigido pelo Mercado Pago)
      const expiryParts = formData.cardExpiry.replace(/\s/g, '').split('/');
      let expiryMonth = (expiryParts[0] || '').replace(/\D/g, '').padStart(2, '0').slice(0, 2);
      let expiryYear = (expiryParts[1] || '').replace(/\D/g, '');
      if (expiryYear.length === 1) expiryYear = '0' + expiryYear;
      if (expiryYear.length === 2) expiryYear = '20' + expiryYear;
      if (expiryMonth.length !== 2 || expiryYear.length !== 4) {
        setLoading(false);
        setStep('form');
        toast.error('Data de validade inválida. Use MM/AA (ex: 12/28).');
        return;
      }

      const cardData = {
        cardNumber: cardNumberClean,
        cardholderName: formData.cardName.trim(),
        cardExpirationMonth: expiryMonth,
        cardExpirationYear: expiryYear,
        securityCode: formData.cardCvv.replace(/\D/g, '').slice(0, 4),
        identificationType: 'CPF',
        identificationNumber: formData.cpf.replace(/\D/g, '').slice(0, 11)
      };

      let tokenResponse;
      try {
        tokenResponse = await mpRef.current.createCardToken(cardData);
      } catch (tokenError: any) {
        const msg = tokenError?.message || tokenError?.cause?.message || '';
        let userMsg = msg
          ? (msg.includes('card') || msg.includes('cartão') ? msg : `Erro ao validar cartão: ${msg}`)
          : 'Erro ao gerar token do cartão. Verifique número, validade (MM/AA), CVV e CPF.';
        if (MP_PUBLIC_KEY.startsWith('TEST-')) {
          userMsg += ' Para cartão real, use chave de produção (APP_USR-...) em VITE_MP_PUBLIC_KEY no .env.';
        }
        throw new Error(userMsg);
      }

      if (!tokenResponse || !tokenResponse.id) {
        throw new Error('Token do cartão inválido. Verifique os dados do cartão.');
      }

      // Determinar payment_method_id baseado no número do cartão
      let paymentMethodId = cardInfo.paymentMethodId;
      if (!paymentMethodId) {
        // Fallback baseado no primeiro dígito
        const firstDigit = cardNumberClean.charAt(0);
        if (firstDigit === '4') paymentMethodId = 'visa';
        else if (firstDigit === '5') paymentMethodId = 'master';
        else if (firstDigit === '3') paymentMethodId = 'amex';
        else paymentMethodId = 'master'; // default
      }

      // Assinatura recorrente (renovação a cada 30 dias no Mercado Pago)
      const subscriptionData = {
        plano_id: String(plano.id),
        valor: Number(plano.valor),
        card_token: tokenResponse.id,
        email: formData.email || '',
        cpf: formData.cpf.replace(/\D/g, '')
      };

      const response = await fetch(`${API_BASE_URL}/mercadopago/assinar-transparente`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(subscriptionData)
      });

      const data = await response.json();

      if (response.ok && ['approved', 'authorized', 'pending', 'in_process'].includes(data.status)) {
        setStep('success');
        setTimeout(() => {
          onSuccess(data);
        }, 2000);
      } else {
        setStep('error');
        // Tratar erro de validação (422) que vem como array
        let errorMsg = 'Pagamento não aprovado';
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            errorMsg = data.detail.map((e: any) => e.msg || e.message || String(e)).join(', ');
          } else if (typeof data.detail === 'string') {
            errorMsg = data.detail;
          } else if (typeof data.detail === 'object') {
            errorMsg = data.detail.msg || data.detail.message || JSON.stringify(data.detail);
          }
        } else if (data.message) {
          errorMsg = data.message;
        }
        setErrorMessage(errorMsg);
      }
    } catch (error: any) {
      setStep('error');
      setErrorMessage(error.message || 'Erro ao processar pagamento');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Tela de processamento
  if (step === 'processing') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
        <Card className="w-full max-w-md mx-2 sm:mx-4 bg-protec-dark-light border-protec-gold/20">
          <CardContent className="p-8 sm:p-12 text-center">
            <Loader2 className="w-12 h-12 sm:w-16 sm:h-16 text-protec-gold animate-spin mx-auto mb-4 sm:mb-6" />
            <h2 className="text-lg sm:text-xl font-bold text-white mb-2">Processando pagamento...</h2>
            <p className="text-gray-400 text-sm sm:text-base">Aguarde enquanto confirmamos</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Tela de sucesso
  if (step === 'success') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
        <Card className="w-full max-w-md mx-2 sm:mx-4 bg-protec-dark-light border-green-500/30">
          <CardContent className="p-8 sm:p-12 text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-green-500" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Pagamento Aprovado!</h2>
            <p className="text-gray-400 mb-4 text-sm sm:text-base">Sua assinatura foi ativada</p>
            <p className="text-protec-gold font-bold text-base sm:text-lg">
              {plano.nome} - {formatCurrency(plano.valor)}/mês
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Tela de erro
  if (step === 'error') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
        <Card className="w-full max-w-md mx-2 sm:mx-4 bg-protec-dark-light border-red-500/30">
          <CardContent className="p-8 sm:p-12 text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
              <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 text-red-500" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Pagamento Recusado</h2>
            <p className="text-gray-400 mb-4 sm:mb-6 text-sm sm:text-base">{errorMessage}</p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Button
                variant="outline"
                className="border-gray-600 text-gray-300"
                onClick={onCancel}
              >
                Cancelar
              </Button>
              <Button
                className="bg-protec-gold text-black"
                onClick={() => setStep('form')}
              >
                Tentar novamente
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Formulário de pagamento
  return (
    <div className="fixed inset-0 z-50 bg-black/80 overflow-y-auto">
  <div className="min-h-screen flex justify-center px-2 sm:px-4 pt-16 sm:pt-24 pb-16 sm:pb-24">
    <Card
      className="
        w-full
        max-w-md
        bg-protec-dark-light
        border-protec-gold/20
        flex
        flex-col
        h-fit
      "
    >
      {/* HEADER */}
      <CardHeader className="border-b border-protec-gold/20 py-2 sm:py-3 px-3 sm:px-4 shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2 text-sm sm:text-base">
            <CreditCard className="w-4 h-4 text-protec-gold" />
            Pagamento
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-white h-7 w-7 p-0"
            onClick={onCancel}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>

      {/* CONTEÚDO */}
      <CardContent className="p-4 overflow-y-auto scrollbar-hide">
        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Número do Cartão */}
          <div>
            <Label className="text-gray-300 text-sm">Número do Cartão</Label>
            <Input
              type="text"
              placeholder="0000 0000 0000 0000"
              value={formData.cardNumber}
              onChange={(e) => handleInputChange('cardNumber', e.target.value)}
              maxLength={19}
              className="bg-protec-dark border-gray-600 text-white mt-1 font-mono h-9 text-sm"
              required
            />
            {cardInfo.paymentMethodId && (
              <p className="text-xs text-protec-gold mt-0.5">
                Bandeira: {cardInfo.paymentMethodId.toUpperCase()}
              </p>
            )}
          </div>

          {/* Validade e CVV */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-300 text-sm">Validade</Label>
              <Input
                type="text"
                placeholder="MM/AA"
                value={formData.cardExpiry}
                onChange={(e) => handleInputChange('cardExpiry', e.target.value)}
                maxLength={5}
                className="bg-protec-dark border-gray-600 text-white mt-1 h-9 text-sm"
                required
              />
            </div>
            <div>
              <Label className="text-gray-300 text-sm">CVV</Label>
              <Input
                type="text"
                placeholder="123"
                value={formData.cardCvv}
                onChange={(e) => handleInputChange('cardCvv', e.target.value)}
                maxLength={4}
                className="bg-protec-dark border-gray-600 text-white mt-1 h-9 text-sm"
                required
              />
            </div>
          </div>

          {/* Nome no Cartão */}
          <div>
            <Label className="text-gray-300 text-sm">Nome no Cartão</Label>
            <Input
              type="text"
              placeholder="Nome como está no cartão"
              value={formData.cardName}
              onChange={(e) =>
                handleInputChange('cardName', e.target.value.toUpperCase())
              }
              className="bg-protec-dark border-gray-600 text-white mt-1 uppercase h-9 text-sm"
              required
            />
          </div>

          {/* CPF */}
          <div>
            <Label className="text-gray-300 text-sm">CPF do Titular</Label>
            <Input
              type="text"
              placeholder="000.000.000-00"
              value={formData.cpf}
              onChange={(e) => handleInputChange('cpf', e.target.value)}
              maxLength={14}
              className="bg-protec-dark border-gray-600 text-white mt-1 h-9 text-sm"
              required
            />
          </div>

          {/* Resumo */}
          <div className="bg-protec-dark/80 rounded-lg p-3 mt-2 border border-protec-gold/20">
            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-xs">Plano</span>
                <span className="text-white font-bold text-sm">{plano.nome}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-xs">Cobrança</span>
                <span className="text-white text-sm">Mensal</span>
              </div>
            </div>
            <div className="border-t border-gray-600/50 my-2"></div>
            <div className="flex justify-between items-center">
              <span className="text-white font-semibold text-sm">Total</span>
              <span className="text-protec-gold text-lg font-bold">
                {formatCurrency(plano.valor)}
              </span>
            </div>
          </div>

          {/* Botão */}
          <Button
            type="submit"
            className="w-full bg-protec-gold text-black hover:bg-protec-gold-light h-11 text-base font-semibold mt-2"
            disabled={loading || !mpReady}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processando...
              </>
            ) : (
              'Pagar ' + formatCurrency(plano.valor)
            )}
          </Button>

          {!mpReady && (
            <p className="text-center text-yellow-500 text-xs">
              Carregando sistema de pagamento...
            </p>
          )}
        </form>
      </CardContent>
    </Card>
  </div>
</div>

  );
};

export default CheckoutTransparente;
